// iter-323: Compact heatmap strip — one coloured cell per BOQ line item.
// Greyscale → amber → emerald based on task_progress. Click to navigate.
import { useNavigate } from 'react-router-dom';

// iter-325: Unified colour bands across heatmap + per-row bars per user spec.
//   0   → grey-200 (Not Started)
//   1-24 → grey-300 (Started)
//   25-49 → red-500
//   50-74 → green-400 (light)
//   75-89 → green-700 (dark)
//   90-99 → blue-800 (dark)
//   100   → purple-600 (violet)
const colourFor = (p) => {
  const n = Number(p || 0);
  if (n >= 100) return 'bg-purple-600 hover:bg-purple-700';
  if (n >= 90)  return 'bg-blue-800 hover:bg-blue-900';
  if (n >= 75)  return 'bg-green-700 hover:bg-green-800';
  if (n >= 50)  return 'bg-green-400 hover:bg-green-500';
  if (n >= 25)  return 'bg-red-500 hover:bg-red-600';
  if (n > 0)    return 'bg-stone-300 hover:bg-stone-400';
  return 'bg-stone-200 hover:bg-stone-300';
};

// iter-325: Public-side variant — single horizontal bar showing overall
// progress (no per-cell rendering). Used by PublicBOQView to save top space
// when there are many BOQ items.
export function OverallProgressBar({ items = [], label = '' }) {
  const lineItems = items.filter(i => !i?.is_header && (i?.item_type || 'item') !== 'header');
  if (!lineItems.length) return null;
  const totalProgress = lineItems.reduce((a, i) => a + (i.task_progress || 0), 0);
  const overallPct = Math.round(totalProgress / lineItems.length);
  return (
    <div className="space-y-1.5" data-testid="overall-progress-bar">
      {label && <div className="text-xs font-medium text-stone-600 uppercase tracking-wide">{label}</div>}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-3 bg-stone-100 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${colourFor(overallPct).split(' ')[0]}`}
            style={{ width: `${Math.min(100, overallPct)}%` }}
          />
        </div>
        <span className="text-sm font-semibold text-stone-800 tabular-nums" data-testid="overall-progress-pct">
          {overallPct}% complete
        </span>
      </div>
    </div>
  );
}

export default function ProgressHeatmap({ items = [], boqId, title = 'Execution heatmap', compact = false }) {
  const navigate = useNavigate();
  // Filter out section-header rows
  const lineItems = items.filter(i => !i?.is_header && (i?.item_type || 'item') !== 'header');
  if (!lineItems.length) {
    return (
      <div className="text-xs text-stone-400 italic" data-testid="progress-heatmap-empty">
        No items yet — heatmap will populate as the BOQ grows.
      </div>
    );
  }
  const total = lineItems.length;
  const completed = lineItems.filter(i => (i.task_progress || 0) >= 100).length;
  const inProgress = lineItems.filter(i => (i.task_progress || 0) > 0 && (i.task_progress || 0) < 100).length;
  const pct = total ? Math.round((lineItems.reduce((a, i) => a + (i.task_progress || 0), 0) / (100 * total)) * 100) : 0;

  return (
    <div className="space-y-2" data-testid="progress-heatmap">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-stone-600 uppercase tracking-wide">{title}</div>
        <div className="text-xs text-stone-500">
          <span className="font-semibold text-stone-800">{pct}%</span> · {completed}/{total} done · {inProgress} active
        </div>
      </div>
      <div className={`grid gap-0.5 ${compact ? 'grid-cols-30' : ''}`} style={{ gridTemplateColumns: `repeat(${Math.min(total, 60)}, minmax(8px, 1fr))` }}>
        {lineItems.map((it, idx) => {
          const tip = `#${idx + 1} ${(it.task_title || it.description || 'Item').slice(0, 40)} — ${it.task_progress || 0}%`;
          const onClick = () => {
            if (boqId) navigate(`/boq-worksheets/${boqId}/edit#item-${it.id || idx}`);
          };
          return (
            <button
              key={it.id || idx}
              type="button"
              title={tip}
              onClick={onClick}
              className={`h-3 rounded-sm transition-colors ${colourFor(it.task_progress)} ${it.task_progress_source === 'vendor' ? 'ring-1 ring-purple-900 ring-offset-0' : ''}`}
              data-testid={`heatmap-cell-${idx}`}
            />
          );
        })}
      </div>
      <div className="flex items-center gap-3 text-[10px] text-stone-400 pt-1">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-stone-200" />0%</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500" />25%</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-400" />50%</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-700" />75%</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-800" />90%</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-purple-600" />100%</span>
        <span className="inline-flex items-center gap-1 ml-2"><span className="w-2 h-2 rounded-sm bg-purple-600 ring-1 ring-purple-900" />Vendor</span>
      </div>
    </div>
  );
}
